import type { ClientApp, TableDetails } from "target_wasm";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { useAddToDatasetStore } from "stores/useAddToDatasetStore";
import {
  isConnectorSource,
  isRecipeResultSource,
} from "components/canvas/utils/roles";
import type { RestrictedTable } from "@/components/main/datasets/AddToDatasetDialog";
import { tableStateRefusal } from "./items";

/** Why these tables cannot go into a dataset, or null. A lone recipe result is
 *  refused; in a selection it is named in the dialog as the connectors are. */
export function addToDatasetRefusal(
  clientApp: ClientApp,
  tableIds: string[],
): string | null {
  const tables = tablesOf(clientApp, tableIds);
  if (tables.length === 0) return "not available";
  if (tables.length === 1) {
    const [table] = tables;
    if (isRecipeResultSource(table.source)) return "recipe result";
    return tableStateRefusal(table.source);
  }
  return null;
}

export function openAddToDataset(tableIds: string[]): void {
  const { clientApp } = useBoundStore.getState();
  if (!clientApp || addToDatasetRefusal(clientApp, tableIds)) return;
  const tables = tablesOf(clientApp, tableIds);
  useAddToDatasetStore.getState().show({
    tableIds: tables.map((t) => t.table_id),
    label:
      tables.length === 1 ? tables[0].table_name : `${tables.length} tables`,
    isCatalogModel: clientApp.peek_model_permissions()?.is_catalog ?? false,
    restrictedTables: tables.flatMap((t): RestrictedTable[] => {
      const reason = isConnectorSource(t.source)
        ? "external"
        : isRecipeResultSource(t.source)
          ? "recipe_result"
          : null;
      return reason
        ? [{ table_id: t.table_id, name: t.table_name, reason }]
        : [];
    }),
  });
}

function tablesOf(clientApp: ClientApp, tableIds: string[]): TableDetails[] {
  return [...new Set(tableIds)].flatMap((id) => {
    try {
      return [clientApp.table_details_by_table_id(id)];
    } catch {
      return [];
    }
  });
}
